import { Github, Linkedin, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'
import { FadeIn } from '../components/ui/FadeIn'
import { useLanguage } from '../contexts/LanguageContext'

export function SiteFooter() {
  const { t } = useLanguage()

  const links = [
    { labelKey: 'footer.privacy', href: '/privacy' },
    { labelKey: 'footer.terms', href: '/terms' },
    { labelKey: 'footer.security', href: '/security' },
    { labelKey: 'footer.contact', href: '/contact' },
  ] as const

  const socials = [
    { label: 'GitHub', href: '#', icon: Github },
    { label: 'LinkedIn', href: '#', icon: Linkedin },
    { label: 'Email', href: 'mailto:contacto@nexusdevtech.com', icon: Mail },
  ] as const

  return (
    <footer className="relative border-t border-white/10 bg-nexus-ink">
      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="grid gap-10 lg:grid-cols-12 lg:items-start">
            <div className="lg:col-span-5">
              <div className="text-lg font-semibold tracking-tight">Nexusdevtech</div>
              <p className="mt-2 max-w-md text-sm text-white/70">
                {t('footer.tagline')}
              </p>
            </div>

            <div className="grid gap-10 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-3">
              <div>
                <div className="text-xs font-semibold tracking-[0.2em] text-white/60">
                  {t('footer.legal')}
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {links.map((l) => (
                    <li key={l.labelKey}>
                      <Link
                        to={l.href}
                        className="text-white/70 hover:text-white transition-colors"
                      >
                        {t(l.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <div className="text-xs font-semibold tracking-[0.2em] text-white/60">
                  {t('footer.social')}
                </div>
                <ul className="mt-4 space-y-2 text-sm">
                  {socials.map((s) => {
                    const Icon = s.icon
                    return (
                      <li key={s.label}>
                        <a
                          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
                          href={s.href}
                        >
                          <Icon className="size-4 text-nexus-cyan" />
                          {s.label}
                        </a>
                      </li>
                    )
                  })}
                </ul>
              </div>

              <div>
                <div className="text-xs font-semibold tracking-[0.2em] text-white/60">
                  {t('footer.status')}
                </div>
                <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                  <div className="flex items-center justify-between">
                    <span>{t('footer.services')}</span>
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block size-2 rounded-full bg-nexus-cyan shadow-[0_0_16px_rgba(0,209,255,0.6)]" />
                      {t('footer.operational')}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-white/60">
                    {t('footer.uptime')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-white/10 pt-6 text-xs text-white/55 sm:flex-row sm:items-center sm:justify-between">
            <div>
              © {new Date().getFullYear()} Nexusdevtech. {t('footer.copyright')}
              <span className="ml-2 text-white/45">
                {t('footer.maps')} <a href="https://www.openstreetmap.org/copyright" className="hover:text-white/70 transition-colors" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors (ODbL)
              </span>
            </div>
            <div className="text-white/55">{t('footer.theme')}</div>
          </div>
        </FadeIn>
      </div>
    </footer>
  )
}


